<template>
  <highcharts
    :ref="`ascHistogram`"
    class="highcharts"
    :options="chartOptions"
  ></highcharts>
</template>

<script>
export default {
  name: 'AscHistogramChart',

  props: {
    options: {
      type: Object,
      required: true,
    },
    thresholds: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      chartRef: null,
      chartOptions: this.options,
      minRangeLine: null,
      minRangeLineXPos: null,
      maxRangeLine: null,
      maxRangeLineXPos: null,
      minRangeRect: null,
      maxRangeRect: null,
      lowWarningLine: null,
      lowWarningLineXPos: null,
      lowCautionLine: null,
      lowCautionLineXPos: null,
      highWarningLine: null,
      highWarningLineXPos: null,
      highCautionLine: null,
      highCautionLineXPos: null,
      lowWarningRect: null,
      lowCautionRect: null,
      highWarningRect: null,
      highCautionRect: null,
      warningPlotBandColor: 'rgba(194,0,0,.4)',
      cautionPlotBandColor: 'rgba(194, 108, 0, 0.4)',
      warningPlotLineColor: 'rgb(194,0,0)',
      cautionPlotLineColor: 'rgb(194,108,0)',
      outOfRangePlotLineColor: 'rgb(0,0,0,.6)',
      outOfRangePlotBandColor: 'rgba(255,255,255,.5)',
    }
  },

  watch: {
    options: {
      handler(newObj) {
        this.chartOptions = newObj
      },
      deep: true,
      immediate: true,
    },
    thresholds: {
      handler() {
        if (this.chartRef) {
          this.drawThresholdLines()
          this.drawThresholdRects()
        }
      },
      deep: true,
      immediate: true,
    },
  },

  mounted() {
    this.chartRef = this.$refs.ascHistogram
    if (this.thresholds) {
      this.drawThresholdLines()
      this.drawThresholdRects()
    }
  },

  methods: {
    drawThresholdLines() {
      // set some consts that apply to all drawing positions
      const yPos = this.chartRef.chart.axes[0].labelGroup.element.firstChild.y
      const baseYPos = -(
        this.chartRef.chart.chartHeight -
        yPos.baseVal[0].value +
        19
      )

      if (this.thresholds.minRange !== null) {
        if (this.minRangeLine !== null) {
          this.minRangeLine.destroy()
          this.minRangeLine = null
        }
        this.minRangeLineXPos = this.calculateTickIntervals(
          this.thresholds.minRange,
        )
        this.minRangeLine = this.chartRef.chart.renderer
          .rect(this.minRangeLineXPos, 0, 2, 100)
          .attr({
            fill: this.outOfRangePlotLineColor,
            height: '100%',
            y: baseYPos,
            zIndex: 7,
          })
          .add()
      } else {
        if (this.minRangeLine !== null) {
          this.minRangeLine.destroy()
          this.minRangeLine = null
        }
      }

      if (this.thresholds.lowWarning !== null) {
        // if the threshold is not blank, erase the lines
        // only if there weren't any previous lines
        if (this.lowWarningLine !== null) {
          this.lowWarningLine.destroy()
          this.lowWarningLine = null
        }
        this.lowWarningLineXPos = this.calculateTickIntervals(
          this.thresholds.lowWarning,
        )
        this.lowWarningLine = this.chartRef.chart.renderer
          .rect(this.lowWarningLineXPos, 0, 2, 100)
          .attr({
            fill: this.warningPlotLineColor,
            height: '100%',
            y: baseYPos,
            zIndex: 7,
          })
          .add()
      } else {
        // if the threshold is not blank, erase the lines
        // only if there weren't any previous lines
        if (this.lowWarningLine !== null) {
          this.lowWarningLine.destroy()
          this.lowWarningLine = null
        }
      }

      if (this.thresholds.lowCaution !== null) {
        // if the threshold is not blank, erase the lines
        // only if there weren't any previous lines
        if (this.lowCautionLine !== null) {
          this.lowCautionLine.destroy()
          this.lowCautionLine = null
        }
        this.lowCautionLineXPos = this.calculateTickIntervals(
          this.thresholds.lowCaution,
        )
        this.lowCautionLine = this.chartRef.chart.renderer
          .rect(this.lowCautionLineXPos, 0, 2, 100)
          .attr({
            fill: this.cautionPlotLineColor,
            height: '100%',
            y: baseYPos,
            zIndex: 7,
          })
          .add()
      } else {
        // if the threshold is not blank, erase the lines
        // only if there weren't any previous lines
        if (this.lowCautionLine !== null) {
          this.lowCautionLine.destroy()
          this.lowCautionLine = null
        }
      }

      if (this.thresholds.highCaution !== null) {
        // if the threshold is not blank, erase the lines
        // only if there weren't any previous lines
        if (this.highCautionLine !== null) {
          this.highCautionLine.destroy()
          this.highCautionLine = null
        }
        this.highCautionLineXPos = this.calculateTickIntervals(
          this.thresholds.highCaution,
        )
        this.highCautionLine = this.chartRef.chart.renderer
          .rect(this.highCautionLineXPos, 0, 2, 100)
          .attr({
            fill: this.cautionPlotLineColor,
            height: '100%',
            y: baseYPos,
            zIndex: 7,
          })
          .add()
      } else {
        // if the threshold is not blank, erase the lines
        // only if there weren't any previous lines
        if (this.highCautionLine !== null) {
          this.highCautionLine.destroy()
          this.highCautionLine = null
        }
      }

      if (this.thresholds.highWarning !== null) {
        // if the threshold is not blank, erase the lines
        // only if there weren't any previous lines
        if (this.highWarningLine !== null) {
          this.highWarningLine.destroy()
          this.highWarningLine = null
        }
        this.highWarningLineXPos = this.calculateTickIntervals(
          this.thresholds.highWarning,
        )
        this.highWarningLine = this.chartRef.chart.renderer
          .rect(this.highWarningLineXPos, 0, 2, 100)
          .attr({
            fill: this.warningPlotLineColor,
            height: '100%',
            y: baseYPos,
            zIndex: 7,
          })
          .add()
      } else {
        // if the threshold is not blank, erase the lines
        // only if there weren't any previous lines
        if (this.highWarningLine !== null) {
          this.highWarningLine.destroy()
          this.highWarningLine = null
        }
      }

      if (this.thresholds.maxRange !== null) {
        if (this.maxRangeLine !== null) {
          this.maxRangeLine.destroy()
          this.maxRangeLine = null
        }
        this.maxRangeLineXPos = this.calculateTickIntervals(
          this.thresholds.maxRange,
        )
        this.maxRangeLine = this.chartRef.chart.renderer
          .rect(this.maxRangeLineXPos, 0, 2, 100)
          .attr({
            fill: this.outOfRangePlotLineColor,
            height: '100%',
            y: baseYPos,
            zIndex: 7,
          })
          .add()
      } else {
        if (this.maxRangeLine !== null) {
          this.maxRangeLine.destroy()
          this.maxRangeLine = null
        }
      }
    },
    drawThresholdRects() {
      // set some consts that apply to all drawing positions
      const yPos = this.chartRef.chart.axes[0].labelGroup.element.firstChild.y
      const firstTickXPos = this.chartRef.chart.plotLeft
      const lastTickXPos = this.chartRef.chart.chartWidth
      const baseYPos = -(
        this.chartRef.chart.chartHeight -
        yPos.baseVal[0].value +
        19
      )

      if (this.thresholds.minRange !== null) {
        if (this.minRangeRect !== null) {
          this.minRangeRect.destroy()
          this.minRangeRect = null
        }
        let minRangeRectXPos = this.calculateTickIntervals(
          this.thresholds.minRange,
        )
        if (minRangeRectXPos === undefined) {
          minRangeRectXPos = firstTickXPos
        }
        this.minRangeRect = this.chartRef.chart.renderer
          .rect(
            0 + 40,
            0,
            minRangeRectXPos - 40,
            this.chartRef.chart.chartHeight,
          )
          .attr({
            fill: this.outOfRangePlotBandColor,
            height: this.chartRef.chart.chartHeight,
            y: baseYPos,
            zIndex: 3,
          })
          .add()
      } else {
        if (this.minRangeRect !== null) {
          this.minRangeRect.destroy()
          this.minRangeRect = null
        }
      }

      // need to draw low warning rectangle...if min range isn't set
      // the rectangle is from the low warning line to the start of the graph
      if (this.thresholds.lowWarning !== null) {
        if (this.lowWarningRect !== null) {
          this.lowWarningRect.destroy()
          this.lowWarningRect = null
        }
        let lowWarningRectXPos
        if (this.thresholds.minRange) {
          lowWarningRectXPos = this.calculateTickIntervals(
            this.thresholds.minRange,
          )
        } else {
          lowWarningRectXPos = firstTickXPos
        }
        if (lowWarningRectXPos === undefined) {
          lowWarningRectXPos = firstTickXPos
        }
        this.lowWarningRect = this.chartRef.chart.renderer
          .rect(
            lowWarningRectXPos,
            0,
            this.lowWarningLineXPos - lowWarningRectXPos,
            this.chartRef.chart.chartHeight,
          )
          .attr({
            fill: this.warningPlotBandColor,
            height: this.chartRef.chart.chartHeight,
            y: baseYPos,
            zIndex: 1,
          })
          .add()
      } else {
        if (this.lowWarningRect !== null) {
          this.lowWarningRect.destroy()
          this.lowWarningRect = null
        }
      }

      let lowCautionRectXPos
      if (this.thresholds.lowCaution !== null) {
        if (this.lowCautionRect !== null) {
          this.lowCautionRect.destroy()
          this.lowCautionRect = null
        }
        if (this.thresholds.lowWarning) {
          lowCautionRectXPos = this.calculateTickIntervals(
            this.thresholds.lowWarning,
          )
        } else if (this.thresholds.minRange) {
          lowCautionRectXPos = this.calculateTickIntervals(
            this.thresholds.minRange,
          )
        } else {
          lowCautionRectXPos = firstTickXPos
        }
        if (lowCautionRectXPos === undefined) {
          lowCautionRectXPos = firstTickXPos
        }
        this.lowCautionRect = this.chartRef.chart.renderer
          .rect(
            lowCautionRectXPos,
            0,
            this.lowCautionLineXPos - lowCautionRectXPos,
            this.chartRef.chart.chartHeight,
          )
          .attr({
            fill: this.cautionPlotBandColor,
            height: this.chartRef.chart.chartHeight,
            y: baseYPos,
            zIndex: 1,
          })
          .add()
      } else {
        if (this.lowCautionRect !== null) {
          this.lowCautionRect.destroy()
          this.lowCautionRect = null
        }
      }

      let highCautionRectEndXPos
      if (this.thresholds.highCaution !== null) {
        if (this.highCautionRect !== null) {
          this.highCautionRect.destroy()
          this.highCautionRect = null
        }
        if (this.thresholds.highWarning) {
          highCautionRectEndXPos = this.calculateTickIntervals(
            this.thresholds.highWarning,
          )
        } else if (this.thresholds.maxRange) {
          highCautionRectEndXPos = this.calculateTickIntervals(
            this.thresholds.maxRange,
          )
        } else {
          highCautionRectEndXPos = lastTickXPos
        }
        if (highCautionRectEndXPos === undefined) {
          highCautionRectEndXPos = lastTickXPos
        }
        this.highCautionRect = this.chartRef.chart.renderer
          .rect(
            this.highCautionLineXPos,
            0,
            highCautionRectEndXPos - this.highCautionLineXPos,
            this.chartRef.chart.chartHeight,
          )
          .attr({
            fill: this.cautionPlotBandColor,
            height: this.chartRef.chart.chartHeight,
            y: baseYPos,
            zIndex: 1,
          })
          .add()
      } else {
        if (this.highCautionRect !== null) {
          this.highCautionRect.destroy()
          this.highCautionRect = null
        }
      }

      let highWarningRectEndXPos
      if (this.thresholds.highWarning !== null) {
        if (this.highWarningRect !== null) {
          this.highWarningRect.destroy()
          this.highWarningRect = null
        }
        if (this.thresholds.maxRange) {
          highWarningRectEndXPos = this.calculateTickIntervals(
            this.thresholds.maxRange,
          )
        } else {
          highWarningRectEndXPos = lastTickXPos
        }
        if (highWarningRectEndXPos === undefined) {
          highWarningRectEndXPos = lastTickXPos
        }
        this.highWarningRect = this.chartRef.chart.renderer
          .rect(
            this.highWarningLineXPos,
            0,
            highWarningRectEndXPos - this.highWarningLineXPos,
            this.chartRef.chart.chartHeight,
          )
          .attr({
            fill: this.warningPlotBandColor,
            height: this.chartRef.chart.chartHeight,
            y: baseYPos,
            zIndex: 1,
          })
          .add()
      } else {
        if (this.highWarningRect !== null) {
          this.highWarningRect.destroy()
          this.highWarningRect = null
        }
      }

      let maxRangeRectEndXPos
      if (this.thresholds.maxRange !== null) {
        if (this.maxRangeRect !== null) {
          this.maxRangeRect.destroy()
          this.maxRangeRect = null
        }
        maxRangeRectEndXPos = lastTickXPos
        this.maxRangeRect = this.chartRef.chart.renderer
          .rect(
            this.maxRangeLineXPos,
            0,
            maxRangeRectEndXPos - this.maxRangeLineXPos,
            this.chartRef.chart.chartHeight,
          )
          .attr({
            fill: this.outOfRangePlotBandColor,
            height: this.chartRef.chart.chartHeight,
            y: baseYPos,
            zIndex: 5,
          })
          .add()
      } else {
        if (this.maxRangeRect !== null) {
          this.maxRangeRect.destroy()
          this.maxRangeRect = null
        }
      }
    },

    calculateTickIntervals(threshold) {
      let drawingXPos
      const plotStartX = this.chartRef.chart.plotLeft
      this.chartOptions.xAxis[0].categories.every((xAxisTick, index, ticks) => {
        const currentTick = parseFloat(ticks[index])
        const nextTick = ticks[index + 1] ? parseFloat(ticks[index + 1]) : null
        if (threshold >= currentTick && threshold <= nextTick) {
          const maxPixelX =
            this.chartRef.chart.series[0].data[index + 1].clientX
          const minPixelX = this.chartRef.chart.series[0].data[index].clientX
          const valueMax = parseFloat(
            this.chartRef.chart.series[0].data[index + 1].category,
          )
          const valueMin = parseFloat(
            this.chartRef.chart.series[0].data[index].category,
          )
          const valueDistance = valueMax - valueMin
          const pixelDistance = maxPixelX - minPixelX
          const newValueDifference = threshold - valueMin
          const increment = (pixelDistance * newValueDifference) / valueDistance
          drawingXPos = plotStartX + minPixelX + increment
          return false
        } else {
          if (threshold === currentTick) {
            drawingXPos =
              plotStartX + this.chartRef.chart.series[0].data[index].clientX
            return false
          } else if (threshold === nextTick) {
            drawingXPos =
              plotStartX + this.chartRef.chart.series[0].data[index + 1].clientX
            return false
          } else {
            return true
          }
        }
      })
      return drawingXPos
    },
  },
}
</script>

<style lang="scss" scoped>
.highcharts {
  width: 100%;
  margin: 0 auto;

  .highcharts-container {
    .highcharts-label {
      span {
        font-size: 13px;
        font-family: inherit !important;
      }
    }
  }
}
</style>
